// SquareDotsSpinner.js
//
// A small, dependency-free "chasing tail" loading indicator: eight dots
// arranged around a 3x3 grid (corners + edge midpoints, centre empty)
// pulsing in sequence so the bright dot appears to walk the perimeter.
//
// Used wherever an in-flight indicator should match the WARA-Ops portal's
// own visual language rather than MUI's generic CircularProgress — e.g.
// the post-login OIDC redirect interstitial and the "Set up & open Argo"
// button on the MyJobs tab.
//
// API
//   <SquareDotsSpinner />
//   <SquareDotsSpinner size="small" />
//   <SquareDotsSpinner size={32} label="Opening Argo…" />
//   <SquareDotsSpinner label="Loading" labelPosition="below" />
//
// Sizing — the named tokens map to:
//     small  -> 24px box (~4px dots)   — inline with body text or button labels
//     medium -> 40px box (~7px dots)   — default
//     large  -> 64px box (~11px dots)  — interstitials, full-page spinners
//   A numeric `size` is taken as the box edge in CSS pixels; dot diameter
//   is derived in the stylesheet (18% of the box).
//
// Colour — the dots use `currentcolor`, so they inherit whatever
// `color:` is in effect on the wrapper or any ancestor:
//     <span style={{ color: '#cfe3ff' }}><SquareDotsSpinner /></span>
//   or, inside a MUI Button, the dots automatically take the button's
//   text colour. No explicit colour prop is needed (and there isn't
//   one) — the surrounding element decides.
//
// Accessibility — the wrapper is role=status with aria-live=polite. When
// `label` is set it doubles as the aria-label, otherwise we fall back to
// "Loading" so screen readers always announce something.

import React from 'react';
import PropTypes from 'prop-types';

import styles from './SquareDotsSpinner.module.css';

// Box-edge in CSS px for each named size token. Single source of truth;
// the CSS module derives dot diameter and inner padding from this value
// via the --sds-size custom property set inline below.
const SIZE_PX = {
    small: 24,
    medium: 40,
    large: 64,
};

/**
 * Resolve the `size` prop (named token or numeric px) to a pixel number.
 * Falls back to medium for anything we don't recognise — defensive rather
 * than throwing, because a stray prop should never break the page that
 * embeds the spinner.
 */
function resolveSizePx(size) {
    if (typeof size === 'number' && Number.isFinite(size) && size > 0) {
        return size;
    }
    if (typeof size === 'string' && Object.prototype.hasOwnProperty.call(SIZE_PX, size)) {
        return SIZE_PX[size];
    }
    return SIZE_PX.medium;
}

// Cell layout for the 3x3 grid. Index N (1..8) walks the perimeter
// clockwise from the top-left; the centre cell is intentionally empty.
// The CSS module's .dot-N classes apply animation-delay in the same
// clockwise order so the bright dot rotates around the square.
//
//   1  2  3
//   8  ·  4
//   7  6  5
//
// Listed row-major so React can map into a flat children array that the
// CSS Grid then lays out without any extra wrappers.
const CELL_ORDER = [1, 2, 3, 8, 0, 4, 7, 6, 5];

function SquareDotsSpinner(props) {
    const {
        size = 'medium',
        label = null,
        labelPosition = 'beside',
        className = '',
        ...rest
    } = props;

    const px = resolveSizePx(size);

    // Inline custom property — keeps the CSS module size-agnostic and
    // lets the same stylesheet drive every named token + arbitrary px.
    const boxStyle = { '--sds-size': `${px}px` };

    const wrapperClass = [
        styles.wrapper,
        labelPosition === 'below' ? styles['wrapper-stacked'] : '',
        className,
    ].filter(Boolean).join(' ');

    // The accessible name. Prefer the visible label so it matches what
    // sighted users see; fall back to a generic "Loading" so AT users
    // never get an empty status announcement.
    const ariaLabel = (typeof label === 'string' && label.length > 0) ? label : 'Loading';

    return (
        <span
            role="status"
            aria-live="polite"
            aria-label={ariaLabel}
            className={wrapperClass}
            {...rest}
            >
            <span className={styles.box} style={boxStyle} aria-hidden="true">
                {CELL_ORDER.map((n, idx) => (
                    <span
                        key={idx}
                        className={
                            n === 0
                                ? `${styles.dot} ${styles['dot-empty']}`
                                : `${styles.dot} ${styles[`dot-${n}`]}`
                        }
                    />
                ))}
            </span>
            {label && (
                <span className={styles.label}>{label}</span>
            )}
        </span>
    );
}

SquareDotsSpinner.propTypes = {
    // Box-edge size. Named tokens map to fixed px values (see SIZE_PX);
    // a number is taken as a CSS-pixel box edge.
    size: PropTypes.oneOfType([
        PropTypes.oneOf(['small', 'medium', 'large']),
        PropTypes.number,
    ]),
    // Optional visible text — also used as the accessible name.
    label: PropTypes.string,
    // Where the label sits relative to the spinner. 'beside' is the
    // default (inline button-style); 'below' is for stacked
    // interstitials / full-page loaders.
    labelPosition: PropTypes.oneOf(['beside', 'below']),
    // Layout/style escape hatch — applied to the outer wrapper.
    className: PropTypes.string,
};

export default SquareDotsSpinner;
